import { ArrowRight } from "lucide-react";
import { articlePath, faqSections, listIndexableArticles } from "../content";
import {
  DEFAULT_LOCALE,
  LOCALE_TAGS,
  localePath,
  useLocale,
  useMessages,
} from "../i18n";
import { faqCrumbs, faqId } from "../seo";
import { ARTICLES_PATH, FAQ_PATH } from "../shared";
import { Breadcrumbs, ReadingShell } from "./ReadingShell";

/**
 * The one page that is genuinely a list of questions, and therefore the one
 * page that emits `FAQPage` structured data. Every question here has a stable
 * anchor, so an answer can be linked to on its own, and the anchor is derived
 * from the question rather than authored beside it.
 *
 * The anchors differ per language, because they are derived from the question
 * and the question is translated. That is correct rather than unfortunate: a
 * Thai anchor on a Thai page is what a Thai reader would share.
 */
export function FaqView() {
  const locale = useLocale();
  const t = useMessages();
  const articles = listIndexableArticles().slice(0, 4);
  const foreign = locale !== DEFAULT_LOCALE;

  return (
    <ReadingShell path={FAQ_PATH}>
      <main className="faq-page">
        <Breadcrumbs crumbs={faqCrumbs(locale)} />

        <header className="faq-page__head">
          <p className="tag tag--sun">{t.faq.tag}</p>
          <h1>{t.faq.title}</h1>
          <p className="faq-page__lede">{t.seo.faqDescription}</p>
        </header>

        {faqSections(locale).map((section) => (
          <section className="faq-page__group" key={section.title}>
            <h2>{section.title}</h2>
            <dl>
              {section.faqs.map((faq) => (
                <div id={faqId(faq.question)} key={faq.question}>
                  <dt>{faq.question}</dt>
                  <dd>{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        <section className="faq-page__more">
          <h2>{t.faq.moreTitle}</h2>
          <p>
            {t.faq.moreBodyBefore}{" "}
            <a href={localePath(locale, ARTICLES_PATH)}>{t.faq.moreBodyLink}</a>{" "}
            {t.faq.moreBodyAfter}
          </p>
          <ul>
            {articles.map((article) => (
              <li key={article.slug}>
                <a
                  href={articlePath(article.slug)}
                  hrefLang={foreign ? LOCALE_TAGS[DEFAULT_LOCALE] : undefined}
                  lang={foreign ? LOCALE_TAGS[DEFAULT_LOCALE] : undefined}
                >
                  <span aria-hidden="true">{article.emoji}</span> {article.title}
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section className="faq-page__cta">
          <h2>{t.faq.ctaTitle}</h2>
          <p>{t.faq.ctaBody}</p>
          <a className="btn btn--primary btn--lg" href={localePath(locale, "/")}>
            <span className="btn__icon" aria-hidden="true">
              <ArrowRight size={19} />
            </span>
            <span>{t.faq.ctaButton}</span>
          </a>
        </section>
      </main>
    </ReadingShell>
  );
}
